import React, { useState, useRef, useEffect } from 'react';
import { generateTxnRef, computeKeyTest, formatUSD, todayISO } from '../utils/keyGenerator';

/**
 * Normalize any JSON structure into flat deposit entries.
 * Supports:
 *  - Flat array: [{ amount, description, ref, date }]
 *  - Xero BankTransactions: { BankTransactions: [{ Total, Contact, ... }] }
 *  - Wrapped objects: { entries, transactions, ledger, data, items, records }
 *  - Single entry object: { amount: 500 }
 */
function normalizeEntries(data) {
  // If it's already a flat array, check if items need normalization
  if (Array.isArray(data)) {
    return data.map(normalizeOneEntry).filter(e => e.amount > 0);
  }

  // Xero BankTransactions format
  if (data.BankTransactions && Array.isArray(data.BankTransactions)) {
    return data.BankTransactions.map(normalizeOneEntry).filter(e => e.amount > 0);
  }

  // Common wrapper keys
  const wrapperKeys = ['entries', 'transactions', 'ledger', 'data', 'items', 'records', 'payments', 'transfers', 'deposits'];
  for (const key of wrapperKeys) {
    if (data[key] && Array.isArray(data[key])) {
      return data[key].map(normalizeOneEntry).filter(e => e.amount > 0);
    }
  }

  // Single entry object — try to normalize it directly
  const single = normalizeOneEntry(data);
  if (single.amount > 0) return [single];

  // Last resort: look for any array property in the object
  for (const key of Object.keys(data)) {
    if (Array.isArray(data[key]) && data[key].length > 0 && typeof data[key][0] === 'object') {
      return data[key].map(normalizeOneEntry).filter(e => e.amount > 0);
    }
  }

  return [];
}

/**
 * Pick the first non-null/undefined value from a list.
 */
function pick(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

/**
 * Normalize a single entry from various formats into a standard flat object.
 */
function normalizeOneEntry(raw) {
  if (!raw || typeof raw !== 'object') return { amount: 0 };

  // --- AMOUNT ---
  // Try direct amount fields first, then Xero Total/SubTotal, then LineItems sum
  let amountRaw = pick(
    raw.amount, raw.Amount,
    raw.value, raw.Value,
    raw.credit, raw.Credit,
    raw.Total, raw.total,
    raw.SubTotal, raw.subTotal, raw.subtotal,
    raw.LineAmount, raw.lineAmount
  );
  // Fallback: sum Xero LineItems
  if (amountRaw === undefined && raw.LineItems && Array.isArray(raw.LineItems)) {
    amountRaw = raw.LineItems.reduce((s, li) => s + parseFloat(li.LineAmount || li.lineAmount || li.Amount || li.amount || 0), 0);
  }
  const amount = parseFloat(amountRaw || 0);

  // --- DESCRIPTION ---
  let descRaw = pick(
    raw.description, raw.Description,
    raw.narration, raw.Narration,
    raw.memo, raw.Memo,
    raw.narrative, raw.Narrative,
    raw.purpose, raw.Purpose
  );
  // Fallback: Xero LineItems description
  if (!descRaw && raw.LineItems && raw.LineItems[0]) {
    descRaw = raw.LineItems[0].Description || raw.LineItems[0].description;
  }
  // Fallback: Xero Type
  if (!descRaw) descRaw = pick(raw.Type, raw.type);
  const description = descRaw || 'Deposit';

  // --- REFERENCE ---
  const reference = pick(
    raw.ref, raw.Ref,
    raw.reference, raw.Reference,
    raw.id, raw.Id, raw.ID,
    raw.BankTransactionID, raw.transactionId, raw.transaction_id,
    raw.invoiceNumber, raw.invoice_number
  ) || '';

  // --- DATE ---
  let date = pick(raw.date, raw.Date, raw.DateString, raw.dateString, raw.valueDate, raw.value_date) || '';
  // Handle Xero /Date(...)/ format
  if (typeof date === 'string' && date.includes('/Date(')) {
    const ms = parseInt(date.replace(/\/Date\((\d+).*\)\//, '$1'));
    if (!isNaN(ms)) date = new Date(ms).toISOString().slice(0, 10);
  }
  // Handle ISO datetime strings
  if (typeof date === 'string' && date.includes('T')) {
    date = date.slice(0, 10);
  }
  if (!date) date = todayISO();

  // --- CURRENCY ---
  let currencyRaw = pick(
    raw.currency, raw.Currency,
    raw.CurrencyCode, raw.currencyCode, raw.currency_code
  );
  if (!currencyRaw && raw.BankAccount) {
    currencyRaw = raw.BankAccount.CurrencyCode;
  }
  const currency = currencyRaw || 'USD';

  // --- PARTY / SENDER ---
  let partyRaw = pick(
    raw.party, raw.Party,
    raw.sender, raw.Sender,
    raw.counterparty, raw.Counterparty
  );
  // Xero Contact
  if (!partyRaw && raw.Contact) {
    partyRaw = raw.Contact.Name || raw.Contact.name;
  }
  if (!partyRaw) {
    partyRaw = pick(raw.contactName, raw.contact_name, raw.beneficiary, raw.Beneficiary);
  }
  const party = partyRaw || 'Ledger Upload';

  // --- BANK ACCOUNT ---
  let bankRaw = pick(
    raw.accountNo, raw.AccountNo, raw.account_no,
    raw.accountNumber, raw.AccountNumber
  );
  if (!bankRaw && raw.BankAccount) {
    bankRaw = raw.BankAccount.Name || raw.BankAccount.AccountID;
  }
  const bankAccount = bankRaw || '';

  // --- STATUS ---
  const status = raw.Status || raw.status || '';

  return { amount, description, reference, date, currency, party, bankAccount, status };
}

export default function Deposit({ addTransaction, wallet, accounts, preSelectedAccount, onAccountUsed }) {
  const [entries, setEntries] = useState([]);
  const [preview, setPreview] = useState(null);
  const [alert, setAlert] = useState(null);
  const [lastDeposits, setLastDeposits] = useState([]);
  const [rawFileName, setRawFileName] = useState('');
  const [targetAccount, setTargetAccount] = useState(preSelectedAccount?.iban || '');
  const [duplicateWarn, setDuplicateWarn] = useState(null);
  const fileRef = useRef();

  // Auto-select target account when navigating from Account page
  useEffect(() => {
    if (preSelectedAccount?.iban) {
      setTargetAccount(preSelectedAccount.iban);
      if (onAccountUsed) onAccountUsed();
    }
  }, [preSelectedAccount, onAccountUsed]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const rows = normalizeEntries(data);
        if (rows.length === 0) {
          setAlert({ type: 'error', msg: 'No valid entries found. Could not detect amount field in the JSON structure.' });
          return;
        }
        setEntries(rows);
        setPreview(rows);
        setRawFileName(file.name);
        setAlert({ type: 'success', msg: `Loaded ${rows.length} entries from ${file.name} — Total: ${formatUSD(rows.reduce((s, e) => s + e.amount, 0))}` });
      } catch {
        setAlert({ type: 'error', msg: 'Invalid JSON file. Please upload a valid ledger JSON.' });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const selectedAcct = accounts?.find(a => a.iban === targetAccount);

  const handleCredit = async (force) => {
    // Call backend with duplicate detection
    try {
      const res = await fetch('/api/deposit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries, accountIban: targetAccount || undefined, fileName: rawFileName, force: !!force }),
      });
      const data = await res.json();
      if (data.duplicate) {
        setDuplicateWarn({ original: data.original, message: data.message });
        setAlert({ type: 'error', msg: 'Duplicate deposit detected — this exact data was already uploaded.' });
        return;
      }
      if (!data.success) {
        setAlert({ type: 'error', msg: data.error || 'Deposit failed' });
        return;
      }
    } catch {}

    setDuplicateWarn(null);
    // Credit each entry to frontend wallet
    const credited = [];
    entries.forEach(e => {
      if (e.amount <= 0) return;
      const txnRef = generateTxnRef('DEP');
      const keyTest = computeKeyTest(e.amount, e.date);
      const tx = addTransaction({
        type: 'DEP',
        creditAmount: e.amount,
        description: e.description,
        ref: e.reference || txnRef,
        counterparty: e.party,
        mt: 'LEDGER',
        keyTest,
        valueDate: e.date,
        currency: e.currency,
        accountIban: targetAccount || null,
        accountHolder: selectedAcct?.holderName || null,
      });
      credited.push({ ...tx, amount: e.amount, keyTest, txnRef });
    });

    const total = credited.reduce((s, c) => s + c.amount, 0);
    const acctLabel = selectedAcct ? ` to account ${selectedAcct.ibanFormatted} (${selectedAcct.holderName})` : '';
    setAlert({ type: 'success', msg: `${credited.length} entries credited${acctLabel}. Total: ${formatUSD(total)}` });
    setLastDeposits(credited);
    setEntries([]);
    setPreview(null);
  };

  const totalPreview = preview ? preview.reduce((s, e) => s + e.amount, 0) : 0;

  return (
    <div className="gap-16">
      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.type === 'success' ? '\u2713' : '\u2717'} {alert.msg}
        </div>
      )}

      {/* Wallet Balance */}
      <div className="stat-tile gold" style={{ padding: '20px 24px' }}>
        <div className="stat-label">Current Wallet Balance</div>
        <div className="stat-value stat-green">{formatUSD(wallet.balance)}</div>
        <div className="stat-sub">Funds deposited here will credit to this balance</div>
      </div>

      {/* Target Account Selector */}
      {accounts && accounts.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Deposit Target Account</span>
          </div>
          <div className="card-body">
            <select className="form-input" value={targetAccount}
              onChange={e => setTargetAccount(e.target.value)}>
              <option value="">Main Wallet (no specific account)</option>
              {accounts.filter(a => a.status === 'ACTIVE').map(a => (
                <option key={a.iban} value={a.iban}>
                  {a.ibanFormatted} — {a.holderName} ({a.currency}) — Balance: {formatUSD(a.balance)}
                </option>
              ))}
            </select>
            {selectedAcct && (
              <div style={{
                marginTop: 10, padding: '10px 14px', borderRadius: 'var(--radius)',
                background: 'rgba(218,165,32,0.06)', border: '1px solid rgba(218,165,32,0.15)',
                fontSize: 12, color: 'var(--text2)',
              }}>
                Depositing to: <strong style={{ color: 'var(--gold)' }}>{selectedAcct.ibanFormatted}</strong>
                {' — '}{selectedAcct.holderName} ({selectedAcct.typeName || selectedAcct.type})
                {' — '}Current balance: <strong>{formatUSD(selectedAcct.balance)}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Upload Ledger (JSON)</span>
        </div>
        <div className="card-body">
          <input type="file" accept=".json" ref={fileRef} style={{ display: 'none' }} onChange={handleFile} />
          <div className="upload-zone" onClick={() => fileRef.current.click()}>
            <div className="upload-icon">{'\uD83D\uDCC4'}</div>
            <div className="upload-text">Click to upload Ledger JSON file</div>
            <div className="upload-hint">
              Supports: Xero bank transactions, flat ledger arrays, or any JSON with amount fields
            </div>
          </div>
        </div>
      </div>

      {/* Preview Table */}
      {preview && preview.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Preview &mdash; {preview.length} Entries</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-gold btn-sm" onClick={() => handleCredit(false)}>
                Credit All to Wallet
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => { setPreview(null); setEntries([]); setDuplicateWarn(null); }}>
                Clear
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Party</th>
                  <th>Currency</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--gold)' }}>
                      {String(row.reference).slice(0, 30)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{row.date}</td>
                    <td style={{ fontWeight: 500 }}>{row.description}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{row.party}</td>
                    <td style={{ fontSize: 12 }}>{row.currency}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--teal)', fontFamily: "'IBM Plex Mono', monospace" }}>
                      {formatUSD(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{
            padding: '14px 24px', borderTop: '1px solid var(--border-light)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>
              {preview.length} entries ready to credit
              {rawFileName && <span style={{ color: 'var(--text3)', marginLeft: 8 }}>from {rawFileName}</span>}
            </span>
            <span style={{
              fontFamily: "'Source Serif 4', serif",
              fontSize: 20, color: 'var(--gold)', fontWeight: 700
            }}>
              Total: {formatUSD(totalPreview)}
            </span>
          </div>
        </div>
      )}

      {/* Duplicate Warning */}
      {duplicateWarn && (
        <div className="card" style={{ border: '2px solid rgba(245,158,11,0.4)' }}>
          <div className="card-header" style={{ background: 'rgba(245,158,11,0.06)' }}>
            <span className="card-title" style={{ color: '#D97706' }}>{'\u26A0'} Duplicate Deposit Detected</span>
            <button className="btn btn-outline btn-sm" onClick={() => setDuplicateWarn(null)}>Dismiss</button>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.8, marginBottom: 16 }}>
              This exact deposit data was already uploaded and processed:<br />
              <strong>Processed at:</strong> {new Date(duplicateWarn.original.depositedAt).toLocaleString()}<br />
              <strong>Entries:</strong> {duplicateWarn.original.entries} | <strong>Total amount:</strong> ${duplicateWarn.original.totalAmount.toFixed(2)}<br />
              {duplicateWarn.original.fileName && duplicateWarn.original.fileName !== 'manual' && <><strong>Original file:</strong> {duplicateWarn.original.fileName}<br /></>}
              {duplicateWarn.original.accountIban && <><strong>Account:</strong> {duplicateWarn.original.accountIban}<br /></>}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-gold" onClick={() => handleCredit(true)}>
                Deposit Anyway (Override Duplicate Check)
              </button>
              <button className="btn btn-outline" onClick={() => { setDuplicateWarn(null); setPreview(null); setEntries([]); }}>
                Cancel &amp; Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Last Deposits Record */}
      {lastDeposits.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Last Deposit Record &mdash; {lastDeposits.length} entries credited</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Txn Reference</th>
                  <th>Description</th>
                  <th>Key Test</th>
                  <th style={{ textAlign: 'right' }}>Amount Credited</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lastDeposits.map((d, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--gold)' }}>{d.id || d.txnRef}</td>
                    <td>{d.description}</td>
                    <td><span className="badge badge-settled" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{d.keyTest}</span></td>
                    <td className="text-right" style={{ color: 'var(--teal)', fontWeight: 600 }}>+{formatUSD(d.amount)}</td>
                    <td><span className="badge badge-settled">SETTLED</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* JSON Format Reference */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Supported JSON Formats</span>
        </div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.8 }}>
            The system auto-detects and normalizes multiple JSON formats:
            <strong> Xero BankTransactions</strong>,
            <strong> flat ledger arrays</strong>,
            <strong> wrapped objects</strong> (entries, transactions, data, items), and
            <strong> single entry objects</strong>.
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 8 }}>FLAT ARRAY FORMAT</div>
              <div className="code-block">{`[
  {
    "amount": 10000.00,
    "currency": "USD",
    "date": "2026-03-28",
    "description": "Wire Transfer",
    "reference": "REF-001",
    "party": "CLIENT CO LTD"
  }
]`}</div>
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 8 }}>XERO BANK TRANSACTIONS</div>
              <div className="code-block">{`{
  "BankTransactions": [
    {
      "Total": 10000.00,
      "CurrencyCode": "USD",
      "DateString": "2026-03-28",
      "Contact": { "Name": "..." },
      "Reference": "HBUK629..."
    }
  ]
}`}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
